import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SupportEmailLink } from "components/app/support-email-link";
import { UpsellBanner } from "components/app/upsell-banner";
import { ContentFull, ContentHeader, ContentSubheader } from "components/layout";
import { PipelineSummary } from "components/referral-pipelines/types";
import { trpc } from "components/rpc/trpc";
import { DeleteButton } from "components/ui/action-button";
import { AutosavingInput } from "components/ui/autosaving-input";
import { ErrorMessage } from "components/ui/error-message";
import { InlineBanner } from "components/ui/inline-banner";
import { Input } from "components/ui/input";
import { InternalLink } from "components/ui/internal-link";
import { PrimaryButton } from "components/ui/primary-button";
import { Spinner } from "components/ui/spinner";
import { Toggle } from "components/ui/toggle";
import React, { useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useProductName } from "store/selectors/branding";
import { useFeature } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { ReferralPipelineStageEditor } from "./referral-pipeline-stage-editor";

export const ReferralPipelinesSettings: React.FC<unknown> = () => {
    const referralPipelinesEnabled = useFeature("referralPipelines");

    if (!referralPipelinesEnabled) {
        return null;
    }

    return <ReferralPipelinesSettingsContent />;
};

const ReferralPipelinesSettingsContent: React.FC = () => {
    const trpcClient = trpc.useTRPC();
    const queryClient = useQueryClient();
    const pipelinesQuery = useQuery({ ...trpcClient.pipeline.list.queryOptions() });
    const canCreateQuery = useQuery({ ...trpcClient.pipeline.canCreate.queryOptions() });
    const createMutation = useMutation({
        ...trpcClient.pipeline.create.mutationOptions(),
    });
    const [newPipelineName, setNewPipelineName] = React.useState("");

    const refetchPipelines = useCallback(async () => {
        await queryClient.refetchQueries({
            queryKey: trpcClient.pipeline.list.queryKey(),
        });
        await queryClient.refetchQueries({
            queryKey: trpcClient.pipeline.canCreate.queryKey(),
        });
    }, [queryClient, trpcClient]);

    const handleNewPipelineNameChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setNewPipelineName(e.target.value);
        },
        [setNewPipelineName],
    );

    const atPipelineLimit = canCreateQuery.data === "TooMany";
    const productName = useProductName();

    const addPipeline = useCallback(async () => {
        const name = newPipelineName.trim();
        if (!name || createMutation.isPending || atPipelineLimit) {
            return;
        }
        await createMutation.mutateAsync({ name });
        handleEvent("referral-pipeline-create");
        setNewPipelineName("");
        await refetchPipelines();
    }, [
        newPipelineName,
        createMutation,
        atPipelineLimit,
        setNewPipelineName,
        refetchPipelines,
    ]);

    const handleAddPipeline = useCallback(
        async (e: React.MouseEvent<HTMLButtonElement>) => {
            e.preventDefault();
            await addPipeline();
        },
        [addPipeline],
    );

    const handlePipelineNameKeyDown = useCallback(
        async (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter") {
                e.preventDefault();
                await addPipeline();
            }
        },
        [addPipeline],
    );

    const location = useLocation();
    const addPipelineRef = React.useRef<HTMLDivElement>(null);
    const pipelineNameInputRef = React.useRef<HTMLInputElement>(null);
    React.useEffect(() => {
        if (pipelinesQuery.isPending || location.hash !== "#add-pipeline") {
            return;
        }
        addPipelineRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        pipelineNameInputRef.current?.focus({ preventScroll: true });
    }, [location.hash, pipelinesQuery.isPending]);

    if (pipelinesQuery.isPending) {
        return <Spinner />;
    }

    if (pipelinesQuery.isError) {
        return (
            <ErrorMessage component="ReferralPipelinesSettings">
                <pre>{JSON.stringify(pipelinesQuery.error, null, 2)}</pre>
            </ErrorMessage>
        );
    }

    return (
        <ContentFull>
            <ContentHeader>Referral pipelines</ContentHeader>
            <ContentSubheader>
                Pipelines are boards where referral contacts and referral organizations
                move through stages.
            </ContentSubheader>
            <div className="divide-y divide-gray-200">
                {(pipelinesQuery.data ?? []).map(pipeline => (
                    <ReferralPipelineSettings
                        key={pipeline.id}
                        pipeline={pipeline}
                        onChanged={refetchPipelines}
                    />
                ))}
                <div className="py-6 first:pt-0 last:pb-0" ref={addPipelineRef}>
                    <h3 className="text-lg font-medium text-gray-900">
                        Add referral pipeline
                    </h3>
                    {atPipelineLimit ? (
                        <div className="mt-2">
                            <UpsellBanner source="referral-pipelines">
                                <InlineBanner type="warning">
                                    Your plan includes a single referral pipeline.
                                    Additional pipelines are available to {productName}{" "}
                                    Premium customers. Contact <SupportEmailLink /> to
                                    discuss an upgrade to Premium.
                                </InlineBanner>
                            </UpsellBanner>
                        </div>
                    ) : (
                        <div className="mt-2 flex items-center space-x-4">
                            <div className="w-96">
                                <Input
                                    ref={pipelineNameInputRef}
                                    placeholder="Enter pipeline name"
                                    value={newPipelineName}
                                    onChange={handleNewPipelineNameChange}
                                    onKeyDown={handlePipelineNameKeyDown}
                                />
                            </div>
                            <PrimaryButton onClick={handleAddPipeline}>
                                Add pipeline
                            </PrimaryButton>
                        </div>
                    )}
                </div>
            </div>
        </ContentFull>
    );
};

interface ReferralPipelineSettingsProps {
    pipeline: PipelineSummary;
    onChanged: () => void;
}

const ReferralPipelineSettings: React.FC<ReferralPipelineSettingsProps> = props => {
    const { pipeline, onChanged } = props;
    const trpcClient = trpc.useTRPC();
    const renameMutation = useMutation({
        ...trpcClient.pipeline.rename.mutationOptions(),
    });
    const deleteMutation = useMutation({
        ...trpcClient.pipeline.delete.mutationOptions(),
    });
    const setAutoAddMutation = useMutation({
        ...trpcClient.pipeline.setAutoAdd.mutationOptions(),
    });

    const handleRename = useCallback(
        async (name: string) => {
            const trimmed = name.trim();
            if (!trimmed) {
                return;
            }
            await renameMutation.mutateAsync({
                pipelineId: pipeline.id,
                name: trimmed,
            });
            handleEvent("referral-pipeline-rename");
            onChanged();
        },
        [renameMutation, pipeline.id, onChanged],
    );

    const handleDelete = useCallback(async () => {
        await deleteMutation.mutateAsync({ pipelineId: pipeline.id });
        handleEvent("referral-pipeline-delete");
        onChanged();
    }, [deleteMutation, pipeline.id, onChanged]);

    const handleAutoAddChange = useCallback(
        async (autoAddNewReferralSources: boolean) => {
            await setAutoAddMutation.mutateAsync({
                pipelineId: pipeline.id,
                autoAddNewReferralSources,
            });
            handleEvent("referral-pipeline-auto-add-toggle", {
                enabled: autoAddNewReferralSources,
            });
            onChanged();
        },
        [setAutoAddMutation, pipeline.id, onChanged],
    );

    return (
        <div className="py-6 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                    Configure &quot;{pipeline.name}&quot;
                </h3>
                <div className="flex items-center space-x-2 lg:space-x-4">
                    <InternalLink to={`/pipelines/${pipeline.id}`} className="text-sm">
                        View pipeline
                    </InternalLink>
                    <DeleteButton
                        backgroundColor="bg-white"
                        confirm={{
                            title: "Delete pipeline",
                            message: `Are you sure you want to delete the "${pipeline.name}" pipeline? Its board and stages will be removed.`,
                        }}
                        onClick={handleDelete}
                    />
                </div>
            </div>
            <div className="mt-2 w-96">
                <AutosavingInput
                    label="Pipeline name"
                    initial={pipeline.name}
                    onCommit={handleRename}
                />
            </div>
            <div className="mt-4">
                <Toggle
                    label="Automatically add new referral sources to this pipeline"
                    checked={pipeline.autoAddNewReferralSources}
                    onChange={handleAutoAddChange}
                />
                <p className="mt-1 text-sm text-gray-500">
                    New referral contacts and organizations are added to the first stage
                    of this pipeline when they are created.
                </p>
            </div>
            <div className="mt-4">
                <h4 className="text-sm font-semibold text-gray-700">Stages</h4>
                <p className="text-sm text-gray-500 mb-2">
                    Stages appear as columns on the pipeline board.
                </p>
                <ReferralPipelineStageEditor pipeline={pipeline} onChanged={onChanged} />
            </div>
        </div>
    );
};
